import * as React from 'react';
import {
  CSSProperties,
  useContext,
  useRef,
  useState,
  UIEvent,
  MouseEvent,
  KeyboardEvent,
  useEffect,
} from 'react';
import Arrow from '../../../Icons/Arrows/Arrow';
import { StoreContext } from '../../../../services/store';
import { Direction, IMedia } from '../../../../types';
import { infiniteScrollTimeout, modalCarouselTransitionDurationSec } from '../../../consts';
import { useCarouselDimensions } from '../../../../hooks/useCarouselDimensions';
import { useCompactDesktopWidth, getCompactContainerStyle } from '../../../../hooks/useCompactDesktopWidth';

interface Props<T> {
  items: T[];
  nbElems: number;
  style?: CSSProperties;
  onPostChange: (post: IMedia | null) => void;
}

interface State {
  slidePosition: number;
  squareSize: number;
  canSlideLeft: boolean;
  canSlideRight: boolean;
  activeIndex: number;
}

const ModalCarousel = (props: Props<JSX.Element>) => {
  const context = useContext(StoreContext);
  const slideRef = useRef<HTMLDivElement>(null);
  const scrollTimeout = useRef<NodeJS.Timeout>();
  const hasUserInteracted = useRef(false);
  const cloneCount = Math.min(props.nbElems, props.items.length);
  let lastScrollValue = 0;
  let lastSlidePosition = 0;

  const [carouselState, setCarouselState] = useState<State>({
    slidePosition: 0, // index of the first visible content
    squareSize: 0,
    canSlideLeft: false,
    canSlideRight: props.items.length > props.nbElems,
    activeIndex: 0, // active index for mobile infinite scrolling
  });

  const contentSize = context.data.settings.content_size;
  const { width: constrainedWidgetWidth, isCompact } = useCompactDesktopWidth({
    widgetWidth: context.widgetWidth,
    contentSize,
    isMobile: context.isMobile,
  });
  const compactStyle = getCompactContainerStyle(isCompact, constrainedWidgetWidth);

  const carouselDimensions = useCarouselDimensions({
    widgetWidth: constrainedWidgetWidth,
    slideRef,
    hideArrows: context.config.hideArrows || false,
    hasItems: props.nbElems < props.items.length,
    nbElems: props.nbElems,
    itemCount: props.items.length,
    isMobile: context.isMobile,
    forcedArrowSize: isCompact ? 'medium' : undefined,
  });
  const arrowSize = isCompact ? 'medium' : carouselDimensions.arrowSize;

  useEffect(() => {
    if (carouselDimensions.itemWidth !== carouselState.squareSize) {
      setCarouselState(prevState => ({
        ...prevState,
        squareSize: carouselDimensions.itemWidth,
      }));
    }
  }, [carouselDimensions.itemWidth]);

  useEffect(() => {
    context.setStoreState((state) => ({
      ...state,
      canSlideLeft: false,
      canSlideRight: props.items.length > props.nbElems,
      carouselSlidePosition: 0,
      carouselContentSize: props.nbElems,
    }));
  }, []);

  const carouselStyles = {
    ...carouselStyle,
    ...props.style,
    ...(compactStyle ?? {}),
  };

  useEffect(() => {
    const handleResize = () => {
      // Force re-calculation by updating a dependency
      setCarouselState(prevState => ({ ...prevState }));
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const node = slideRef.current;
    if (!node) return;

    const markInteraction = () => {
      hasUserInteracted.current = true;
    };

    node.addEventListener('pointerdown', markInteraction, { passive: true });
    node.addEventListener('touchstart', markInteraction, { passive: true });
    node.addEventListener('wheel', markInteraction, { passive: true });

    return () => {
      node.removeEventListener('pointerdown', markInteraction);
      node.removeEventListener('touchstart', markInteraction);
      node.removeEventListener('wheel', markInteraction);
    };
  }, [context.isMobile]);

  useEffect(() => {
    const evtListener = ({ detail }: { detail: Direction }) => {
      onArrowClick(undefined, detail, carouselState);
    };
    // @ts-ignore
    window.addEventListener('adalongWidget_slide', evtListener);
    return () => {
      // @ts-ignore
      window.removeEventListener('adalongWidget_slide', evtListener);
    };
  }, [carouselState]);

  useEffect(() => {
    const maxPosition = props.items.length - numElems() + 1;
    const boundedPosition = Math.min(maxPosition, carouselState.slidePosition);
    const slidePosition = Math.max(boundedPosition, 0);
    setCarouselState({
      ...carouselState,
      squareSize: carouselState.squareSize,
      slidePosition,
      canSlideLeft: slidePosition > 0,
      canSlideRight: slidePosition < props.items.length - props.nbElems,
    });
    context.setStoreState((state) => ({
      ...state,
      canSlideLeft: slidePosition > 0,
      canSlideRight: slidePosition < props.items.length - props.nbElems,
      carouselSlidePosition: slidePosition,
      carouselContentSize: props.nbElems,
    }));
  }, [props.items.length]);

  const renderNativeScroll = () => (
    <div 
      style={nativeScrollSlideStyle} 
      ref={slideRef} 
      onScroll={onMobileScroll}
    >
      <div style={getNativeScrollSlideContainerStyle(context.isMobile, props.nbElems, props.items.length)}>
        {renderMedia()}
      </div>
    </div>
  );

  function displayArrowNavorNativeScroll() {
    if (context.isMobile) {
      return context.config.displayMobileCarouselArrows
        ? renderArrowNavigation()
        : renderNativeScroll();
    }
    return renderArrowNavigation();
  }

  const renderArrowNavigation = () => {
    const showArrows = props.nbElems < props.items.length && !context.config.hideArrows;
    const contentSizeSlots = Math.max(props.nbElems - props.items.length, 0);
    // When content size is not reachedd, shift items so the empty space is split evenly left/right
    const contentSizeNotReachedOffset = contentSizeSlots > 0 ? (contentSizeSlots * carouselState.squareSize) / 2 : 0;
    const leftPosition = -(carouselState.slidePosition * carouselState.squareSize) + contentSizeNotReachedOffset;

    return (
      <>
        <Arrow
          direction='left'
          enabled={carouselState.canSlideLeft}
          onClick={(e) => onArrowClick(e, 'left')}
          show={showArrows}
          classPrefix={context.config.classPrefix}
          size={arrowSize}
        />

        <div style={desktopSlideStyle} ref={slideRef}>
          <div
            style={{
              ...desktopSlideContainerStyle,
              left: leftPosition,
            }}
          >
            {renderMedia()}
          </div>
        </div>

        <Arrow
          direction='right'
          enabled={carouselState.canSlideRight}
          onClick={(e) => onArrowClick(e, 'right')}
          show={showArrows}
          classPrefix={context.config.classPrefix}
          size={arrowSize}
        />
      </>
    );
  };

  const renderMedia = (): JSX.Element[] => {
    // Mobile behavior : Wrapped array for infinite scroll: last items + all items + first items
    if (context.isMobile && props.items.length > 1) {
      const cloneStart = props.items.slice(-cloneCount).map((item, i) => renderSlide(item, i - cloneCount, true));
      const realSlides = props.items.map((media, i) => renderSlide(media, i, true));
      const cloneEnd = props.items.slice(0, cloneCount).map((item, i) => renderSlide(item, props.items.length + i, true));
      return [...cloneStart, ...realSlides, ...cloneEnd];
    }
    
    // Desktop behavior
    return props.items.map((item, i) => renderSlide(item, i, false));
  };

  const renderSlide = (item: JSX.Element, index: number, isMobile: boolean) => {
    if (isMobile) {
      return (
        <div
          style={{
            ...thumbnailStyle,
            width: carouselDimensions.itemWidth,
            scrollSnapAlign: 'center', 
            scrollSnapStop: 'always', 
          }}
          key={`wrapped-${index}`}
        >
          {item}
        </div>
      );
    }

    return (
      <div
        style={{
          ...thumbnailStyle,
          width: carouselDimensions.itemWidth,
          scrollSnapAlign: 'center', 
          scrollSnapStop: 'always', 
        }}
        key={index}
      >
        {item}
      </div>
    );
  }

  function numElems(): number {
    return props.nbElems;
  }

  // Handle initial scroll position
  const centerCurrentItem = () => {
    if (!slideRef.current || !context.isMobile) {
      return;
    }

    const currentPosition = context.currentPosition || 0;
    const totalItems = props.items.length;
    
    // Calculate the scroll position considering clones
    let targetPosition = currentPosition;
    if (totalItems > 1) {
      // Check if we need to jump to a clone for smoother transition
      const diff = Math.abs(currentPosition - carouselState.activeIndex);
        // Check if diff is higher than the number of visible items
        if (diff > props.nbElems) {
          // If so, go to the clone
        if (currentPosition > carouselState.activeIndex) {
            // If the current position is higher, go to the clone on the left
            targetPosition = currentPosition - totalItems;
          } else {
            // If the current position is lower, go to the clone on the right
            targetPosition = currentPosition + totalItems;
          }
        }
      }
      
      // Calculate scroll position with offset for the prepended items
      const offset = Math.min(props.nbElems, totalItems) * carouselDimensions.itemWidth;
      const scrollPosition = offset + targetPosition * carouselDimensions.itemWidth;
      
      // Update the active index after calculating position
      setCarouselState(state => ({
        ...state,
        activeIndex: currentPosition,
      }));
      
      // Apply the scroll position
      slideRef.current.scrollLeft = scrollPosition;
  };
  
  const onMobileScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!slideRef.current) {
      return;
    }

    const scrollValue = event.currentTarget.scrollLeft;
    
    // Debounce the scroll handling to prevent over-sensitivity
    clearTimeout(scrollTimeout.current);
    
    scrollTimeout.current = setTimeout(() => {
      const totalItems = props.items.length;

      if (context.isMobile && totalItems > 1) {
        // Mobile infinite scrolling logic
        infiniteScroll(event, scrollValue);
      } else {
        // Original behavior for non-infinite scroll
        nonInfiniteScroll(event, scrollValue);
      }
    }, infiniteScrollTimeout); // Timeout to debounce without noticeable delay
  };

  const infiniteScroll = (event: UIEvent<HTMLDivElement>, scrollValue: number) => {
    const totalItems = props.items.length;

    // Calculate position considering the wrapped items
    const offset = Math.min(props.nbElems, totalItems) * carouselDimensions.itemWidth;
    const adjustedScrollValue = scrollValue - offset;
    const slidePosition = Math.round(adjustedScrollValue / carouselDimensions.itemWidth);

    // Ensure slideRef.current is not null
    if (!slideRef.current) return;
  
    // Check if we've reached the beginning or the end for infinite scroll
    if (slidePosition < 0) {
      // Jump to the last position
      slideRef.current.style.scrollBehavior = 'auto';
      slideRef.current.scrollLeft = (totalItems - 1) * carouselDimensions.itemWidth + offset;
      setTimeout(() => {
        if (slideRef.current) slideRef.current.style.scrollBehavior = 'smooth';
      }, infiniteScrollTimeout);
    } else if (slidePosition >= totalItems) {
      // Jump to the first position
      slideRef.current.style.scrollBehavior = 'auto';
      slideRef.current.scrollLeft = offset;
      setTimeout(() => {
        if (slideRef.current) slideRef.current.style.scrollBehavior = 'smooth';
      }, infiniteScrollTimeout);
    } else {
      // Valid slide position, apply the real position
      const realPosition = (slidePosition + totalItems) % totalItems;
      const direction = scrollValue > lastScrollValue ? 'left' : 'right';
      lastScrollValue = scrollValue;
      lastSlidePosition = realPosition;

      // Update the store state and internal state
      setCarouselState(state => ({
        ...state,
        activeIndex: realPosition,
      }));

      context.setStoreState((prevState) => ({
        ...prevState,
        currentPosition: realPosition,
        canSlideLeft: true,
        canSlideRight: true,
        carouselSlidePosition: realPosition,
        carouselContentSize: props.nbElems,
      }));

      if (context.post) {
        // We don't want to open the post details if there is no opened post
        const media = context.data.content.medias[realPosition];
        props.onPostChange(media);
      }

      if (hasUserInteracted.current) {
        context.triggerEvent(
          'carouselNativeScroll',
          {
            direction,
            position: realPosition,
            originalEvent: event.nativeEvent,
          },
          context.data.id,
        );
      }
    }
  };  

  const nonInfiniteScroll = (event: UIEvent<HTMLDivElement>, scrollValue: number) => {
    const slidePosition = Math.round(scrollValue / carouselDimensions.itemWidth);

    // Only handle significant changes to avoid micro-adjustments
    if (Math.abs(lastSlidePosition - slidePosition) >= 1) {
      const dir = scrollValue > lastScrollValue ? 'left' : 'right';
      lastScrollValue = scrollValue;
      lastSlidePosition = slidePosition;

      // Update the store state with the current position
      context.setStoreState((prevState) => ({
        ...prevState,
        currentPosition: slidePosition,
        canSlideLeft: slidePosition > 0,
        canSlideRight: slidePosition < props.items.length - numElems(),
        carouselSlidePosition: slidePosition,
        carouselContentSize: numElems(),
      }));

      if (hasUserInteracted.current) {
        context.triggerEvent(
          'carouselNativeScroll',
          {
            direction: dir,
            position: lastSlidePosition,
            originalEvent: event.nativeEvent,
          },
          context.data.id,
        );
      }
    }
  }

  // Center the current item when the component mounts or currentPosition changes
  useEffect(() => {
    if (context.isMobile) {
      centerCurrentItem();
      
      if (props.items.length > 1) {
        context.setStoreState((prevState) => ({
          ...prevState,
          canSlideLeft: true,
          canSlideRight: true,
        }));
      }
    }
  }, [context.currentPosition, carouselDimensions.itemWidth]);

  type ArrowClickEvent = MouseEvent<HTMLOrSVGElement> | KeyboardEvent<HTMLOrSVGElement> | undefined;

  function onArrowClick(
    event: ArrowClickEvent,
    dir: 'right' | 'left',
    state?: State,
    shift?: number,
    shouldUpdateCurrentPosition = true,
  ) {
    if (!slideRef.current) {
      return;
    }

    const max = props.items.length;
    const width = slideRef.current.clientWidth;

    let { slidePosition } = state ?? carouselState;
    const { canSlideRight, canSlideLeft } = state ?? carouselState;

    const move = Math.floor(width / carouselDimensions.itemWidth);

    if (dir === 'right' && canSlideRight) {
      slidePosition = shift ? shift + move : slidePosition + move;
    } else if (dir === 'left' && canSlideLeft) {
      slidePosition = shift ? shift - move : slidePosition - move;
    }
    slidePosition = Math.max(0, Math.min(max - move, slidePosition));
    setCarouselState({
      ...carouselState,
      squareSize: carouselDimensions.itemWidth,
      slidePosition,
      canSlideLeft: slidePosition > 0,
      canSlideRight: slidePosition < max - move,
    });
    context.setStoreState((prevState) => ({
      ...prevState,
      canSlideLeft: slidePosition > 0,
      canSlideRight: slidePosition < max - move,
      carouselSlidePosition: slidePosition,
      carouselContentSize: move,
    }));
    if (shouldUpdateCurrentPosition) {
      context.setStoreState((prevState) => ({
        ...prevState,
        currentPosition: slidePosition,
      }));
    }
    if (event) {
      context.triggerEvent(
        'carouselArrowClicked',
        {
          position: slidePosition,
          originalEvent: event.nativeEvent,
          direction: dir,
        },
        context.data.id,
      );
    }
  }

  return (
    <div 
      className={`${context.config.classPrefix}-layout-carousel`} 
      style={carouselStyles}
    >
      {displayArrowNavorNativeScroll()}
    </div>
  );
};

const carouselStyle: CSSProperties = {
  width: '100%',
  display: 'flex',
  position: 'relative',
  minHeight: 'fit-content',
  alignItems: 'center',
};

const desktopSlideStyle: CSSProperties = {
  flex: 1,
  height: 'auto',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
};

const desktopSlideContainerStyle: CSSProperties = {
  position: 'relative',
  height: 'auto',
  top: 0,
  left: 0,
  transition: `left ${modalCarouselTransitionDurationSec}s`,
  display: 'flex',
};

const nativeScrollSlideStyle: CSSProperties = {
  height: 'auto',
  minWidth: '100%',
  overflowX: 'scroll',
  overflowY: 'hidden',
  whiteSpace: 'nowrap',
  position: 'relative',
  display: 'flex',
  scrollSnapType: 'x mandatory',
  scrollBehavior: 'smooth',
  WebkitOverflowScrolling: 'touch', // iOS scrolling
  msOverflowStyle: 'none',  // Hide scrollbar in IE/Edge
  scrollbarWidth: 'none',   // Hide scrollbar in Firefox
};

const getNativeScrollSlideContainerStyle = (isMobile: boolean, nbElems: number, totalItems: number): CSSProperties => ({
  position: 'relative',
  height: 'auto',
  display: 'flex',
  ...(isMobile && nbElems === 1 && totalItems === 1
      ? { 
          paddingLeft: '0',
          paddingRight: '0',
        } 
      : {})
});

const thumbnailStyle: CSSProperties = {
  display: 'inline-block',
  height: 'auto',
  verticalAlign: 'top',
};

export default ModalCarousel;
